import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Plus, Trash2, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES } from "@/data/campaigns";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import { createCampaign } from "@/lib/api";
import { CoverUploader, GalleryUploader, VideoUploader } from "@/components/MediaUploaders";
import CampaignWriterModal from "@/components/ai/CampaignWriterModal";

const STEPS = [
  { id: 1, title: "Basics", desc: "Title, category, and location" },
  { id: 2, title: "Funding", desc: "Goal and timeline" },
  { id: 3, title: "Story", desc: "Tell your story with media" },
  { id: 4, title: "Rewards", desc: "Design your reward tiers" },
  { id: 5, title: "Preview", desc: "Review and publish" }
];

const StartCampaign = () => {
  const [step, setStep] = useState(1);
  const [aiOpen, setAiOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    title: "", category: "", location: "",
    goal: "", days: "30",
    story: "",
    cover: "", gallery: [], video_url: "",
    rewards: [{ price: "", title: "", description: "" }]
  });
  const navigate = useNavigate();
  const { user, loading: authLoading, requireAuth } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    if (!user) requireAuth("Sign in to launch a campaign on Dwinov.");
  }, [user, authLoading, requireAuth]);

  // Auto-populate from AI draft handoff (session storage set by the writer's Export button)
  useEffect(() => {
    if (searchParams.get("ai") !== "1") return;
    try {
      const raw = sessionStorage.getItem("dwinov:aiDraft");
      if (!raw) return;
      const d = JSON.parse(raw);
      setForm((f) => ({
        ...f,
        title:    d.title    || f.title,
        category: d.suggested_category || f.category,
        story:    d.story    || f.story,
        goal:     d.suggested_funding_goal ? String(d.suggested_funding_goal) : f.goal,
        rewards:  Array.isArray(d.reward_tiers) && d.reward_tiers.length
          ? d.reward_tiers.map((r) => ({
              price: String(r.price || ""),
              title: r.title || "",
              description: r.description || "",
            }))
          : f.rewards,
      }));
      sessionStorage.removeItem("dwinov:aiDraft");
      toast.success("AI draft loaded. Review and tweak before publishing.");
    } catch (e) {
      /* ignore malformed handoff */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateReward = (i, k, v) => setForm(f => ({ ...f, rewards: f.rewards.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addReward = () => setForm(f => ({ ...f, rewards: [...f.rewards, { price: "", title: "", description: "" }] }));
  const removeReward = (i) => setForm(f => ({ ...f, rewards: f.rewards.filter((_, idx) => idx !== i) }));

  const next = () => setStep(s => Math.min(s + 1, STEPS.length));
  const back = () => setStep(s => Math.max(s - 1, 1));

  const publish = async () => {
    if (!requireAuth("Sign in to publish your campaign.")) return;
    if (!form.title || !form.category || !form.goal) {
      toast.error("Please complete title, category, and funding goal");
      return;
    }
    try {
      const payload = {
        title: form.title,
        tagline: form.story?.slice(0, 120) || "",
        category: (CATEGORIES.find((c) => c.id === form.category)?.name) || form.category,
        cover: form.cover || "",
        gallery: form.gallery || [],
        video_url: form.video_url || "",
        story: form.story || "",
        location: form.location || "",
        goal: parseInt(form.goal, 10),
        days_left: parseInt(form.days, 10),
        rewards: form.rewards
          .filter((r) => r.price && r.title)
          .map((r, i) => ({
            id: `r${i + 1}`, price: parseInt(r.price, 10), title: r.title,
            description: r.description || "", claimed: 0, limit: 1000, estimated: "TBD",
          })),
      };
      await createCampaign(payload);
      toast.success("Campaign submitted for review!", { description: "We'll email you within 48 hours." });
      setTimeout(() => navigate("/dashboard"), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create campaign");
    }
  };

  return (
    <div data-testid="start-campaign-page" className="min-h-screen bg-cream-100/70">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-2">
          <img src="/dwinov-logo.webp" alt="Dwinov" className="h-9 w-auto object-contain" />
          <span className="text-sm font-medium text-saffron-600">Create your campaign</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">Launch something great.</h1>
            <p className="mt-2 text-navy-600">A guided flow to publish your campaign in under 20 minutes.</p>
          </div>
          <button
            type="button"
            onClick={() => setAiOpen(true)}
            data-testid="start-generate-with-ai"
            className="btn-saffron inline-flex items-center gap-2 rounded-2xl h-11 px-5 font-semibold self-start md:self-auto"
          >
            <Sparkles className="h-4 w-4" /> Generate with AI
          </button>
        </div>

        <CampaignWriterModal open={aiOpen} onClose={() => setAiOpen(false)} />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Stepper */}
          <div className="lg:col-span-4">
            <ol className="space-y-2" data-testid="step-list">
              {STEPS.map((s) => {
                const active = s.id === step;
                const done = s.id < step;
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => setStep(s.id)}
                      className={`w-full text-left flex items-start gap-3 p-4 rounded-2xl border transition-colors ${active ? "border-saffron-500 bg-white shadow-sm" : done ? "border-warm-200 bg-white" : "border-transparent hover:bg-white hover:border-warm-200"}`}
                    >
                      <span className={`grid place-items-center h-7 w-7 rounded-full text-xs font-semibold flex-shrink-0 ${done ? "bg-emerald-100 text-emerald-700" : active ? "bg-saffron-600 text-white" : "bg-slate-100 text-navy-600"}`}>
                        {done ? <Check className="h-4 w-4" /> : s.id}
                      </span>
                      <div>
                        <div className={`font-medium ${active ? "text-navy-800" : "text-navy-700"}`}>{s.title}</div>
                        <div className="text-xs text-navy-500 mt-0.5">{s.desc}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <div className="rounded-2xl border border-warm-200 bg-white p-8">
              {step === 1 && (
                <div className="space-y-6" data-testid="step-basics">
                  <div>
                    <Label className="text-sm font-medium">Project title</Label>
                    <Input
                      data-testid="input-title"
                      value={form.title}
                      onChange={(e) => update("title", e.target.value)}
                      placeholder="A concise, punchy title"
                      className="mt-2 h-12 rounded-2xl border-warm-200"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Category</Label>
                    <Select value={form.category} onValueChange={(v) => update("category", v)}>
                      <SelectTrigger data-testid="select-category" className="mt-2 h-12 rounded-2xl border-warm-200"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Location</Label>
                    <Input
                      data-testid="input-location"
                      value={form.location}
                      onChange={(e) => update("location", e.target.value)}
                      placeholder="City, Country"
                      className="mt-2 h-12 rounded-2xl border-warm-200"
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6" data-testid="step-funding">
                  <div>
                    <Label className="text-sm font-medium">Funding goal (USD)</Label>
                    <Input
                      data-testid="input-goal"
                      type="number"
                      value={form.goal}
                      onChange={(e) => update("goal", e.target.value)}
                      placeholder="50000"
                      className="mt-2 h-12 rounded-2xl border-warm-200"
                    />
                    <p className="mt-2 text-xs text-navy-500">All-or-nothing: your card is only charged if you meet your goal.</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Campaign duration</Label>
                    <Select value={form.days} onValueChange={(v) => update("days", v)}>
                      <SelectTrigger className="mt-2 h-12 rounded-2xl border-warm-200"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days (recommended)</SelectItem>
                        <SelectItem value="45">45 days</SelectItem>
                        <SelectItem value="60">60 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6" data-testid="step-story">
                  <div>
                    <Label className="text-sm font-medium">Cover image</Label>
                    <div className="mt-2">
                      <CoverUploader value={form.cover} onChange={(v) => update("cover", v)} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Gallery</Label>
                    <div className="mt-2">
                      <GalleryUploader value={form.gallery} onChange={(v) => update("gallery", v)} max={6} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Pitch video</Label>
                    <div className="mt-2">
                      <VideoUploader value={form.video_url} onChange={(v) => update("video_url", v)} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Your story</Label>
                    <Textarea
                      data-testid="input-story"
                      value={form.story}
                      onChange={(e) => update("story", e.target.value)}
                      placeholder="Why does this project matter? What are you building? Who is it for?"
                      className="mt-2 min-h-[220px] rounded-2xl border-warm-200"
                    />
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-6" data-testid="step-rewards">
                  {form.rewards.map((r, i) => (
                    <div key={i} className="rounded-2xl border border-warm-200 p-6">
                      <div className="flex justify-between items-center mb-4">
                        <div className="font-medium">Reward tier {i + 1}</div>
                        {form.rewards.length > 1 && (
                          <button onClick={() => removeReward(i)} className="text-slate-400 hover:text-red-500 transition-colors" aria-label="Remove reward">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-1">
                          <Label className="text-xs">Price ($)</Label>
                          <Input type="number" value={r.price} onChange={(e) => updateReward(i, "price", e.target.value)} className="mt-1 h-11 rounded-xl border-warm-200" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Title</Label>
                          <Input value={r.title} onChange={(e) => updateReward(i, "title", e.target.value)} className="mt-1 h-11 rounded-xl border-warm-200" />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs">Description</Label>
                        <Textarea value={r.description} onChange={(e) => updateReward(i, "description", e.target.value)} className="mt-1 rounded-xl border-warm-200 min-h-[80px]" />
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" onClick={addReward} className="w-full rounded-2xl" data-testid="add-reward-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add another reward
                  </Button>
                </div>
              )}

              {step === 5 && (
                <div className="space-y-6" data-testid="step-preview">
                  <div className="rounded-2xl bg-cream-100 p-6">
                    <div className="text-xs text-navy-500 uppercase tracking-wide">Preview</div>
                    <div className="mt-2 font-display text-2xl font-semibold text-navy-800">{form.title || "Your campaign title"}</div>
                    <div className="mt-1 text-sm text-navy-500">{form.category || "Category"} · {form.location || "Location"}</div>
                    <div className="mt-4 flex gap-6 text-sm">
                      <div><span className="font-medium text-navy-800">${form.goal || "0"}</span><span className="text-navy-500"> goal</span></div>
                      <div><span className="font-medium text-navy-800">{form.days}</span><span className="text-navy-500"> days</span></div>
                      <div><span className="font-medium text-navy-800">{form.rewards.length}</span><span className="text-navy-500"> rewards</span></div>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-saffron-200 bg-saffron-50/40 p-6 text-sm text-navy-700">
                    Before you publish, our team reviews every campaign within 48 hours. You'll receive an email once it's live.
                  </div>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between pt-6 border-t border-warm-200">
                <Button variant="ghost" onClick={back} disabled={step === 1} className="rounded-2xl">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                {step < STEPS.length ? (
                  <Button onClick={next} className="rounded-2xl bg-saffron-600 hover:bg-saffron-700" data-testid="next-step-btn">
                    Continue <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={publish} className="rounded-2xl bg-saffron-600 hover:bg-saffron-700" data-testid="publish-btn">
                    Publish campaign
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartCampaign;
